export interface GuideMeta {
  id: string;
  nav: string;
  eyebrow: string;
  title: string;
  lead?: string;
  marketing?: { label: string; to: string };
}

export interface GuideGroup {
  id: string;
  label: string;
  blurb: string;
  guideIds: string[];
}

/** Guides grouped by the app's coaching journeys (dashboard/discovery,
 *  event creation, event lifecycle, check-in, onboarding) — not by page. */
export const GUIDE_GROUPS: GuideGroup[] = [
  {
    id: 'setup',
    label: 'Getting set up',
    blurb: 'Account, profile, and your first motorcycle.',
    guideIds: ['account-profile', 'add-motorcycle', 'profile-preferences'],
  },
  {
    id: 'discovery',
    label: 'Finding rides',
    blurb: 'Browse the map, check details, and RSVP.',
    guideIds: ['join-event'],
  },
  {
    id: 'hosting',
    label: 'Creating & hosting',
    blurb: 'From drafting an event to check-in and chat on ride day.',
    guideIds: ['event-journey', 'create-event', 'edit-cancel', 'manage-participants', 'check-in', 'host-chat'],
  },
  {
    id: 'riding',
    label: 'Riding together',
    blurb: 'Live tracking and staying safe on the road.',
    guideIds: ['grouppulse', 'ride-safely'],
  },
  {
    id: 'community',
    label: 'Community',
    blurb: 'Reputation, milestones, and boundaries between riders.',
    guideIds: ['achievements', 'blocking-reporting'],
  },
  {
    id: 'plans',
    label: 'Plans & limits',
    blurb: 'What free includes, what Pro adds, and how limits work.',
    guideIds: ['free-pro'],
  },
];

export const GUIDES: Record<string, GuideMeta> = {
  'account-profile': {
    id: 'account-profile',
    nav: 'Account & profile',
    eyebrow: 'Getting set up',
    title: 'Set Up Your Account & Profile',
    lead: 'Your profile is how other riders recognize and trust you.',
    marketing: { label: 'Getting Started', to: '/getting-started' },
  },
  'add-motorcycle': {
    id: 'add-motorcycle',
    nav: 'Add a motorcycle',
    eyebrow: 'Getting set up',
    title: 'How to Add a Motorcycle',
    lead: 'Choose your motorcycle and add the details you want riders to recognize.',
    marketing: { label: 'Garage', to: '/garage' },
  },
  'profile-preferences': {
    id: 'profile-preferences',
    nav: 'Profile & preferences',
    eyebrow: 'Getting set up',
    title: 'Customize Your Experience',
    lead: "You're in control. Set your preferences during onboarding and adjust them anytime.",
    marketing: { label: 'Getting Started', to: '/getting-started' },
  },
  'join-event': {
    id: 'join-event',
    nav: 'Find & join',
    eyebrow: 'Finding rides',
    title: 'Find and Join an Event',
    marketing: { label: 'Events', to: '/events' },
  },
  'event-journey': {
    id: 'event-journey',
    nav: 'Event journey',
    eyebrow: 'Creating & hosting',
    title: 'Follow an event from start to finish',
    lead: 'A connected route through the existing ride-event guides, with host and rider responsibilities.',
  },
  'create-event': {
    id: 'create-event',
    nav: 'Create an event',
    eyebrow: 'Creating & hosting',
    title: 'How to Create an Event',
    lead: 'Five simple steps to get your ride organized.',
    marketing: { label: 'Events', to: '/events' },
  },
  'edit-cancel': {
    id: 'edit-cancel',
    nav: 'Edit or cancel',
    eyebrow: 'Creating & hosting',
    title: 'Event Updates & Cancellation',
    lead: 'Flexible management when plans change.',
    marketing: { label: 'Hosting', to: '/hosting' },
  },
  'manage-participants': {
    id: 'manage-participants',
    nav: 'Manage participants',
    eyebrow: 'Creating & hosting',
    title: 'Managing Participants',
    marketing: { label: 'Hosting', to: '/hosting' },
  },
  'check-in': {
    id: 'check-in',
    nav: 'Check-in',
    eyebrow: 'Creating & hosting',
    title: 'Three Ways to Check In Riders',
    lead: 'Flexible check-in options for every situation.',
    marketing: { label: 'Hosting', to: '/hosting' },
  },
  'host-chat': {
    id: 'host-chat',
    nav: 'Host chat',
    eyebrow: 'Creating & hosting',
    title: 'Host Chat & Moderation',
    marketing: { label: 'Hosting', to: '/hosting' },
  },
  'grouppulse': {
    id: 'grouppulse',
    nav: 'GroupPulse',
    eyebrow: 'Riding together',
    title: 'Ride with GroupPulse',
    marketing: { label: 'GroupPulse', to: '/grouppulse' },
  },
  'ride-safely': {
    id: 'ride-safely',
    nav: 'Ride safely',
    eyebrow: 'Riding together',
    title: 'Safety First',
    marketing: { label: 'Getting Started', to: '/getting-started' },
  },
  'achievements': {
    id: 'achievements',
    nav: 'Achievements',
    eyebrow: 'Community',
    title: 'How Achievements Work',
    marketing: { label: 'Community', to: '/social' },
  },
  'blocking-reporting': {
    id: 'blocking-reporting',
    nav: 'Block & report',
    eyebrow: 'Community',
    title: 'Blocking, Reporting & Shared Events',
    lead: 'Three different controls for three different situations: what each one changes, and what it does not.',
    marketing: { label: 'Privacy & Visibility', to: '/privacy' },
  },
  'free-pro': {
    id: 'free-pro',
    nav: 'Free & Pro',
    eyebrow: 'Plans & limits',
    title: 'Free & Pro Plans',
    lead: 'What each plan includes and the two kinds of limits you might run into.',
    marketing: { label: 'Pro', to: '/pro' },
  },
};
